// =============================================
// Help Guide Content — מדריך עזרה מלא
// KipiKid - תוכן מדריך למשתמש לכל חלקי המערכת
// =============================================

import {
  adminOrgHelpTexts,
  adminManagerHelpTexts,
  adminStaffHelpTexts,
  adminKindergartenHelpTexts,
  adminProductHelpTexts,
  adminCategoryHelpTexts,
  adminCalendarHelpTexts,
  adminKgCalendarHelpTexts,
  adminSettingsHelpTexts,
  parentLoginHelpTexts,
  parentContactsHelpTexts,
  parentRegistrationHelpTexts,
  teacherBudgetHelpTexts,
  teacherExpenseHelpTexts,
  teacherInvoiceHelpTexts,
  teacherPurchaseHelpTexts,
  loginHelpTexts,
  teacherLoginHelpTexts,
  changePasswordHelpTexts,
  callCenterHelpTexts,
} from './helpTexts';

// טיפוסים
export type UserRole = 'parent' | 'staff' | 'admin' | 'tablet' | 'sysadmin' | 'teacher';

export interface GuideFieldRef {
  name: string;
  tooltip: string;
  helper?: string;
}

export interface GuideSection {
  id: string;
  title: string;
  icon: string;
  roles: UserRole[];
  routePattern?: string;
  image?: string;
  introduction: string;
  steps: string[];
  tips?: string[];
  fields: GuideFieldRef[];
}

// המרת אובייקט helpTexts לרשימת שדות
function toFields(texts: Record<string, { tooltip: string; helper?: string }>, labels: Record<string, string>): GuideFieldRef[] {
  return Object.entries(texts).map(([key, val]) => ({
    name: labels[key] || key,
    tooltip: val.tooltip,
    helper: val.helper,
  }));
}

// ===== כל הסקציות =====
export const guideSections: GuideSection[] = [
  // --- כניסה (מנהלים/מוקד) ---
  {
    id: 'login',
    title: 'כניסה למערכת — מנהלים ומוקד',
    icon: 'LogIn',
    roles: ['admin', 'sysadmin', 'staff'],
    routePattern: '/login',
    image: '/guide-images/login.jpg',

    introduction: 'מסך הכניסה למערכת מיועד למנהלי רשתות, מנהלי ערים ומפעילי מוקד טלפוני. הזן את שם המשתמש והסיסמה שקיבלת מהמנהל/ת שלך. אם שכחת את הסיסמה — פנה למנהל/ת המערכת.',
    steps: [
      'הזן שם משתמש (אותיות באנגלית ומספרים)',
      'הזן סיסמה — ודא שהמקלדת באנגלית',
      'לחץ "כניסה" — אם יש מספר תפקידים תועבר למסך בחירת תפקיד',
      'בכניסה ראשונה — תתבקש לשנות את הסיסמה',
    ],
    tips: [
      'אם הכניסה נכשלת — בדוק שהמקלדת באנגלית ושאין Caps Lock',
      'לאחר 5 ניסיונות כושלים החשבון ייחסם ל-15 דקות',
    ],
    fields: toFields(loginHelpTexts, { username: 'שם משתמש', password: 'סיסמה' }),
  },

  // --- כניסת גננת ---
  {
    id: 'teacher-login',
    title: 'כניסה למערכת — גננת',
    icon: 'LogIn',
    roles: ['teacher'],
    routePattern: '/login',
    image: '/guide-images/teacher-login.jpg',

    introduction: 'מסך הכניסה לגננות. השתמשי בשם המשתמש והסיסמה שקיבלת מהמנהל/ת. לאחר הכניסה תועברי לממשק הגננת עם הכלים לניהול תקציב, הוצאות וחשבוניות.',
    steps: [
      'הזיני שם משתמש',
      'הזיני סיסמה — שימי לב שהמקלדת באנגלית',
      'לחצי "כניסה"',
      'בכניסה ראשונה — תתבקשי לשנות סיסמה',
    ],
    fields: toFields(teacherLoginHelpTexts, { username: 'שם משתמש', password: 'סיסמה' }),
  },

  // --- כניסת הורים ---
  {
    id: 'parent-login',
    title: 'כניסה למערכת — הורים',
    icon: 'LogIn',
    roles: ['parent'],
    routePattern: '/parent/login',
    image: '/guide-images/parent-login.jpg',

    introduction: 'מסך הכניסה להורים. ניתן להתחבר באמצעות טלפון, אימייל או שם משתמש. אם שכחת סיסמה — לחץ על "שכחתי סיסמה" לאיפוס באמצעות SMS.',
    steps: [
      'הזן מזהה: טלפון, אימייל או שם משתמש',
      'הזן סיסמה',
      'לחץ "כניסה"',
      'לאיפוס סיסמה: לחץ "שכחתי סיסמה" → הזן טלפון → קבל קוד OTP → הזן סיסמה חדשה',
    ],
    fields: toFields(parentLoginHelpTexts, {
      identifier: 'מזהה (טלפון/אימייל/שם משתמש)',
      password: 'סיסמה',
      otp: 'קוד אימות SMS',
      newPassword: 'סיסמה חדשה',
      confirmPassword: 'אישור סיסמה',
    }),
  },

  // --- רישום הורה ---
  {
    id: 'parent-registration',
    title: 'רישום הורה חדש',
    icon: 'UserPlus',
    roles: ['parent'],
    routePattern: '/register',
    image: '/guide-images/parent-registration.jpg',

    introduction: 'תהליך הרישום מאפשר להורים חדשים להצטרף למערכת. הרישום נעשה באמצעות אימות טלפון ב-SMS. לאחר הרישום תוכלו לצפות בנתוני הנוכחות של ילדיכם ולנהל אנשי קשר לחירום.',
    steps: [
      'הזן מספר טלפון נייד (10 ספרות)',
      'קבל קוד אימות SMS בן 6 ספרות',
      'הזן את הקוד (תקף ל-60 שניות)',
      'מלא פרטים אישיים: שם, אימייל, שפה',
      'בחר שיטת קבלת התראות (WhatsApp/SMS)',
      'הזן כתובת מגורים (אופציונלי)',
      'לחץ "הרשמה" — תועבר לעמוד הבית',
    ],
    fields: toFields(parentRegistrationHelpTexts, {
      phone: 'טלפון נייד',
      otp: 'קוד אימות',
      firstName: 'שם פרטי',
      lastName: 'שם משפחה',
      email: 'אימייל',
      language: 'שפת ממשק',
      notificationMethod: 'שיטת התראות',
      address: 'כתובת',
    }),
  },

  // --- שינוי סיסמה ---
  {
    id: 'change-password',
    title: 'שינוי סיסמה',
    icon: 'KeyRound',
    roles: ['admin', 'sysadmin', 'teacher', 'parent', 'staff', 'tablet'],
    introduction: 'מסך שינוי סיסמה מופיע בכניסה ראשונה או כשהמערכת דורשת שינוי. הסיסמה החדשה חייבת לעמוד בדרישות האבטחה: לפחות 8 תווים, אות גדולה, אות קטנה, ספרה ותו מיוחד.',
    steps: [
      'הזן את הסיסמה הנוכחית',
      'בחר סיסמה חדשה (מינימום 8 תווים: אות גדולה + קטנה + ספרה + תו מיוחד)',
      'הזן שוב את הסיסמה החדשה לאישור',
      'לחץ "שנה סיסמה"',
    ],
    tips: [
      'הסיסמה החדשה לא יכולה להיות זהה לסיסמה הנוכחית',
      'שמור את הסיסמה במקום בטוח',
    ],
    fields: toFields(changePasswordHelpTexts, {
      currentPassword: 'סיסמה נוכחית',
      newPassword: 'סיסמה חדשה',
      confirmPassword: 'אישור סיסמה',
    }),
  },

  // --- ניהול ארגונים ---
  {
    id: 'admin-organizations',
    title: 'ניהול ארגונים',
    icon: 'Landmark',
    roles: ['admin', 'sysadmin'],
    routePattern: '/(admin|sysadmin)/organizations',
    image: '/guide-images/admin-organizations.jpg',

    introduction: 'מסך ניהול ארגונים מאפשר ליצור ולערוך רשתות גנים, עיריות וגופים ממשלתיים. כל ארגון מכיל גנים, עובדים ומשתמשים. ניתן לנהל מנהלים לכל ארגון ולהגדיר איש קשר ופרטי תקשורת.',
    steps: [
      'לחץ "הוסף ארגון חדש" בראש הדף',
      'מלא שם ארגון, סוג (רשת/עירייה/ממשלתי)',
      'הוסף פרטי איש קשר: שם, טלפון, אימייל',
      'לחץ "שמירה" — הארגון יופיע ברשימה',
      'לעריכה: לחץ על אייקון העיפרון בשורת הארגון',
      'להוספת מנהלים: לחץ על אייקון המנהלים בשורת הארגון',
    ],
    tips: [
      'סוג הארגון קובע את ההיררכיה ואינו ניתן לשינוי לאחר יצירה',
      'ארגון לא פעיל — כל המשתמשים שלו לא יוכלו להתחבר',
    ],
    fields: toFields(adminOrgHelpTexts, {
      name: 'שם הארגון',
      organizationType: 'סוג ארגון',
      contactName: 'איש קשר',
      contactPhone: 'טלפון',
      contactEmail: 'אימייל',
      isActive: 'פעיל',
    }),
  },

  // --- הוספת מנהל ---
  {
    id: 'admin-managers',
    title: 'הוספת מנהל לארגון',
    icon: 'UserCog',
    roles: ['admin', 'sysadmin'],
    routePattern: '/(admin|sysadmin)/organizations',

    introduction: 'הוספת מנהל לארגון מתבצעת מתוך מסך הארגונים. מנהל ארגון יכול לנהל את כל הגנים, העובדים והילדים תחת הארגון. ניתן להגדיר מנהלים מרובים לכל ארגון.',
    steps: [
      'במסך הארגונים — לחץ על אייקון "מנהלים" בשורת הארגון',
      'לחץ "הוסף מנהל חדש"',
      'מלא שם פרטי ושם משפחה',
      'הזן טלפון נייד (10 ספרות)',
      'הזן שם משתמש ייחודי (אנגלית + מספרים)',
      'הגדר סיסמה ראשונית (8+ תווים)',
      'לחץ "שמירה"',
    ],
    tips: [
      'שם המשתמש חייב להיות ייחודי בכל המערכת',
      'המנהל יתבקש לשנות סיסמה בכניסה הראשונה',
    ],
    fields: toFields(adminManagerHelpTexts, {
      firstName: 'שם פרטי',
      lastName: 'שם משפחה',
      phone: 'טלפון',
      email: 'אימייל',
      username: 'שם משתמש',
      password: 'סיסמה',
    }),
  },

  // --- ניהול עובדים ---
  {
    id: 'admin-staff',
    title: 'ניהול עובדים',
    icon: 'Users',
    roles: ['admin', 'sysadmin'],
    routePattern: '/(admin|sysadmin)/staff',
    image: '/guide-images/admin-staff.jpg',

    introduction: 'מסך ניהול עובדים מאפשר ליצור, לערוך ולנהל את כל העובדים בארגון — גננות, סייעות, ומנהלים. כל עובד/ת מקבל/ת תפקיד, שם משתמש וסיסמה. גננות וסייעות משויכות לגן ספציפי, בעוד תפקידי ניהול משויכים לארגון.',
    steps: [
      'לחץ "הוסף עובד/ת" בראש הדף',
      'שלב 1: מלא פרטים אישיים — שם, טלפון, אימייל',
      'שלב 2: בחר תפקיד (גננת/סייעת/מנהל/מפקח וכו\')',
      'שלב 3: אם התפקיד גננת או סייעת — שייך לגן וימי עבודה',
      'הגדר שם משתמש (או השאר ריק ליצירה אוטומטית)',
      'הגדר סיסמה ראשונית',
      'לחץ "שמירה"',
    ],
    tips: [
      'גננת/סייעת חייבות להיות משויכות לגן. תפקידי ניהול (6 ומעלה) לא',
      'ניתן לשייך עובד/ת לגנים שונים בימים שונים',
      'שם משתמש ריק — המערכת תייצר אוטומטית',
      'העלאת תמונה — אופציונלי, תדחס אוטומטית',
    ],
    fields: toFields(adminStaffHelpTexts, {
      firstName: 'שם פרטי',
      lastName: 'שם משפחה',
      phone: 'טלפון',
      email: 'אימייל',
      username: 'שם משתמש',
      password: 'סיסמה',
      role: 'תפקיד',
      dayAssignment: 'שיוך ימים וגנים',
      photo: 'תמונת פרופיל',
    }),
  },

  // --- ניהול גנים ---
  {
    id: 'admin-kindergartens',
    title: 'ניהול גנים',
    icon: 'Building2',
    roles: ['admin', 'sysadmin'],
    routePattern: '/(admin|sysadmin)/kindergartens',
    image: '/guide-images/admin-kindergartens.jpg',

    introduction: 'מסך ניהול גנים מאפשר ליצור ולערוך גנים בארגון. כל גן מכיל קוד ייחודי (לכניסת טאבלט), כתובת, טלפון, קיבולת ושעות פעילות. הגן מופיע בממשק הגננת ובטאבלט לאחר שיוך.',
    steps: [
      'לחץ "הוסף גן חדש"',
      'מלא שם הגן וקוד ייחודי (לטאבלט)',
      'הזן כתובת: עיר, רחוב, מספר',
      'הגדר טלפון ואימייל (אופציונלי)',
      'הזן קיבולת מקסימלית (מספר ילדים)',
      'הגדר שעות פתיחה וסגירה',
      'הוסף הערות פנימיות (אופציונלי)',
      'לחץ "שמירה"',
    ],
    tips: [
      'קוד הגן משמש לחיבור הטאבלט — אותיות גדולות בלבד',
      'שעות ברירת מחדל ניתנות לדריסה ברמת ההגדרות',
      'ניתן להעלות תמונת גן — תדחס אוטומטית',
    ],
    fields: toFields(adminKindergartenHelpTexts, {
      name: 'שם הגן',
      code: 'קוד גן',
      city: 'עיר',
      address: 'כתובת',
      phone: 'טלפון',
      email: 'אימייל',
      director: 'מנהלת הגן',
      capacity: 'קיבולת',
      defaultOpenTime: 'שעת פתיחה',
      defaultCloseTime: 'שעת סגירה',
      notes: 'הערות',
      photo: 'תמונת גן',
    }),
  },

  // --- ניהול מוצרים ---
  {
    id: 'admin-products',
    title: 'קטלוג מוצרים',
    icon: 'Package',
    roles: ['admin', 'sysadmin'],
    routePattern: '/(admin|sysadmin)/products',
    image: '/guide-images/admin-products.jpg',

    introduction: 'קטלוג המוצרים מכיל את כל הפריטים שגננות יכולות להזמין. כל מוצר מכיל שם, קטגוריה, יחידת מידה ומחיר משוער. ניתן לסמן מוצר כ"מחויב" (ינוכה מהתקציב) או לא (תרומות וכד\').',
    steps: [
      'לחץ "הוסף מוצר חדש"',
      'מלא שם המוצר',
      'בחר קטגוריית הוצאה',
      'הזן יחידת מידה (יח\', ק"ג, ליטר, מארז)',
      'הזן מחיר משוער ליחידה',
      'סמן "מוצר מחויב" (ינוכה מתקציב) או לא',
      'לחץ "שמירה"',
    ],
    tips: [
      'מוצר לא פעיל לא יופיע בקטלוג להזמנות חדשות',
      'מוצר לא מחויב — לא ינוכה מהתקציב (תרומות, הלוואות)',
    ],
    fields: toFields(adminProductHelpTexts, {
      name: 'שם המוצר',
      category: 'קטגוריה',
      unit: 'יחידת מידה',
      price: 'מחיר',
      chargeable: 'מוצר מחויב',
      active: 'פעיל',
    }),
  },

  // --- ניהול קטגוריות ---
  {
    id: 'admin-categories',
    title: 'קטגוריות הוצאה',
    icon: 'FolderTree',
    roles: ['admin', 'sysadmin'],
    routePattern: '/(admin|sysadmin)/categories',
    image: '/guide-images/admin-categories.jpg',

    introduction: 'קטגוריות ההוצאה משמשות למיון וסיווג הוצאות ומוצרים. כל קטגוריה מכילה שם, אייקון וסדר הצגה. הקטגוריות מופיעות במסכי ההוצאות והמוצרים.',
    steps: [
      'לחץ "הוסף קטגוריה"',
      'מלא שם הקטגוריה',
      'בחר אייקון מספריית Lucide (לדוגמה: utensils-crossed, package)',
      'הגדר סדר הצגה (מספר נמוך = מוצג ראשון)',
      'לחץ "שמירה"',
    ],
    fields: toFields(adminCategoryHelpTexts, {
      name: 'שם הקטגוריה',
      icon: 'אייקון',
      sortOrder: 'סדר הצגה',
    }),
  },

  // --- לוח שנה ארצי ---
  {
    id: 'admin-calendar',
    title: 'לוח שנה ארצי',
    icon: 'CalendarDays',
    roles: ['admin', 'sysadmin'],
    routePattern: '/(admin|sysadmin)/calendar',
    image: '/guide-images/admin-calendar.jpg',

    introduction: 'לוח השנה הארצי מגדיר חגים, חופשות ומצבי פעילות מיוחדים לכל הגנים. אירועים ארציים משפיעים על מערכת ההתראות — ביום סגור לא יישלחו התראות. ניתן גם להוסיף אירועים ברמת גן ספציפי.',
    steps: [
      'לחץ על תאריך בלוח השנה או "הוסף אירוע"',
      'מלא שם האירוע',
      'בחר סוג: חג, חופשה, חול המועד, אחר',
      'הגדר תאריכי התחלה וסיום',
      'בחר מצב פעילות: סגור, רגיל, או חלקי',
      'הוסף הערות (אופציונלי)',
      'לחץ "שמירה"',
    ],
    tips: [
      'מצב "סגור" — לא יישלחו התראות ביום זה',
      'מצב "חלקי" — הגן פועל בשעות מקוצרות',
      'אירועי גן — לחץ על גן ספציפי להוספת אירוע מקומי',
    ],
    fields: [
      ...toFields(adminCalendarHelpTexts, {
        eventName: 'שם האירוע',
        eventType: 'סוג אירוע',
        startDate: 'תאריך התחלה',
        endDate: 'תאריך סיום',
        operationMode: 'מצב פעילות',
        notes: 'הערות',
      }),
      ...toFields(adminKgCalendarHelpTexts, {
        eventType: 'סוג אירוע (גן)',
        startDate: 'תאריך התחלה (גן)',
        endDate: 'תאריך סיום (גן)',
        title: 'שם אירוע (גן)',
        notes: 'הערות (גן)',
      }),
    ],
  },

  // --- הגדרות מערכת ---
  {
    id: 'admin-settings',
    title: 'הגדרות מערכת',
    icon: 'Settings',
    roles: ['admin', 'sysadmin'],
    routePattern: '/(admin|sysadmin)/settings',
    image: '/guide-images/admin-settings.jpg',

    introduction: 'הגדרות המערכת שולטות בפרמטרים הגלובליים: זמני התראות, שעות פעילות, מרווחי רענון ושפת ברירת מחדל. שינויים בהגדרות משפיעים על כל הגנים בארגון.',
    steps: [
      'גלול בין ההגדרות השונות',
      'שנה ערכים לפי הצורך',
      'לחץ "שמירה" לעדכון',
    ],
    tips: [
      'זמני התראות — משפיעים על מתי נשלחת התראה להורים',
      'שעות ברירת מחדל — ניתנות לדריסה ברמת הגן',
      'שעת המרה — מתי מסך הטאבלט עובר מ"טרם הגיעו" ל"נמצאים"',
      'מרווח רענון — תדירות רענון אוטומטי בטאבלט (30-600 שניות)',
    ],
    fields: toFields(adminSettingsHelpTexts, {
      firstAlertDelay: 'השהיה להתראה ראשונה',
      secondAlertDelay: 'השהיה להתראה שנייה',
      maxAlertDelay: 'זמן מקסימלי להתראות',
      defaultOpenTime: 'שעת פתיחה',
      defaultCloseTime: 'שעת סגירה',
      displaySwitchTime: 'שעת מעבר תצוגה',
      latestArrivalTime: 'שעת הגעה אחרונה',
      refreshInterval: 'מרווח רענון',
      language: 'שפת ברירת מחדל',
    }),
  },

  // --- תקציבים (גננות) ---
  {
    id: 'teacher-budgets',
    title: 'ניהול תקציבים',
    icon: 'Wallet',
    roles: ['teacher'],
    routePattern: '/teacher/budgets',
    image: '/guide-images/teacher-budgets.jpg',

    introduction: 'מסך התקציבים מאפשר לגננת לנהל את מקורות התקציב השונים — רשת, עירייה, ועד הורים, משרד החינוך ותרומות. כל מקור מכיל תקציב כולל, תקופת תוקף ויתרה עדכנית. ההוצאות מנוכות מהתקציב אוטומטית.',
    steps: [
      'לחצי "הוסף תקציב חדש"',
      'מלאי שם מקור התקציב',
      'בחרי סוג מקור (רשת, עירייה, ועד הורים וכו\')',
      'הזיני סכום תקציב כולל',
      'הגדירי תקופת תוקף: תאריך התחלה וסיום',
      'הוסיפי הערות (אופציונלי)',
      'לחצי "שמירה"',
    ],
    tips: [
      'היתרה מתעדכנת אוטומטית בעת הוספת הוצאות',
      'ניתן ליצור מספר מקורות תקציב במקביל',
    ],
    fields: toFields(teacherBudgetHelpTexts, {
      name: 'שם התקציב',
      sourceType: 'סוג מקור',
      totalBudget: 'תקציב כולל',
      periodStart: 'תאריך התחלה',
      periodEnd: 'תאריך סיום',
      notes: 'הערות',
    }),
  },

  // --- הוצאות (גננות) ---
  {
    id: 'teacher-expenses',
    title: 'מעקב הוצאות',
    icon: 'Receipt',
    roles: ['teacher'],
    routePattern: '/teacher/expenses',
    image: '/guide-images/teacher-expenses.jpg',

    introduction: 'מסך ההוצאות מאפשר לגננת לתעד כל הוצאה — רכישות, שירותים ותשלומים. כל הוצאה משויכת למקור תקציב, קטגוריה ותאריך. ההוצאות מנוכות אוטומטית מהתקציב הרלוונטי.',
    steps: [
      'לחצי "הוסף הוצאה חדשה"',
      'בחרי מקור תקציב (ממנו תנוכה ההוצאה)',
      'מלאי תיאור ההוצאה',
      'הזיני סכום',
      'בחרי קטגוריה ותאריך',
      'הוסיפי הערות (אופציונלי)',
      'לחצי "שמירה"',
    ],
    tips: [
      'ניתן לסנן הוצאות לפי מקור תקציב, קטגוריה ותאריך',
      'לעריכה — לחצי על ההוצאה ברשימה',
    ],
    fields: toFields(teacherExpenseHelpTexts, {
      source: 'מקור תקציב',
      description: 'תיאור',
      amount: 'סכום',
      date: 'תאריך',
      category: 'קטגוריה',
      notes: 'הערות',
    }),
  },

  // --- סריקת חשבוניות ---
  {
    id: 'teacher-invoices',
    title: 'סריקת חשבוניות',
    icon: 'ScanLine',
    roles: ['teacher'],
    routePattern: '/teacher/invoices',
    image: '/guide-images/teacher-invoices.jpg',

    introduction: 'מערכת סריקת חשבוניות (OCR) מאפשרת לצלם או להעלות תמונה של חשבונית — המערכת תזהה אוטומטית את פרטי הספק, מספר חשבונית, תאריך וסכום. ניתן לערוך את הנתונים לפני שמירה.',
    steps: [
      'לחצי "סרקי חשבונית"',
      'צלמי חשבונית או העלי תמונה (JPEG/PNG, עד 5MB)',
      'המתיני לזיהוי אוטומטי (OCR)',
      'בדקי ותקני את הפרטים שזוהו: ספק, מספר חשבונית, תאריך, סכום',
      'לחצי "שמירה" — החשבונית תישמר עם ההוצאה',
    ],
    tips: [
      'תמונה ברורה ומוארת — תוצאות זיהוי טובות יותר',
      'ניתן לערוך כל שדה שזוהה ידנית',
      'תמונות גדולות ידחסו אוטומטית',
    ],
    fields: toFields(teacherInvoiceHelpTexts, {
      upload: 'העלאת תמונה',
      vendorName: 'שם הספק',
      vatNumber: 'ח.פ./עוסק מורשה',
      invoiceNumber: 'מספר חשבונית',
      invoiceDate: 'תאריך חשבונית',
      totalAmount: 'סכום כולל',
    }),
  },

  // --- רשימות קניות ---
  {
    id: 'teacher-purchases',
    title: 'רשימות קניות',
    icon: 'ShoppingCart',
    roles: ['teacher'],
    routePattern: '/teacher/purchases',
    image: '/guide-images/teacher-purchases.jpg',

    introduction: 'רשימות קניות מאפשרות לגננת להכין רשימת מוצרים להזמנה. ניתן להוסיף מוצרים מהקטלוג או ידנית. לאחר קבלת המוצרים, ניתן להמיר את הרשימה להוצאה ולנכות מהתקציב.',
    steps: [
      'לחצי "רשימה חדשה"',
      'תני שם לרשימה (לדוגמה: "רכישות שבועיות")',
      'שייכי מקור תקציב',
      'הוסיפי מוצרים מהקטלוג — חפשי לפי שם',
      'או הוסיפי מוצר ידנית: שם, כמות, יחידה, מחיר',
      'לאחר קבלת המוצרים — לחצי "המר להוצאה"',
      'בחרי מקור תקציב ועדכני כמויות שהתקבלו',
      'לחצי "אשרי" — ההוצאה תנוכה מהתקציב',
    ],
    tips: [
      'מוצר "לא מחויב" (תרומה) לא ינוכה מהתקציב',
      'ניתן לערוך כמויות שהתקבלו אם שונות מההזמנה',
      'רשימה שהומרה — מוצגת בסטטוס "הושלם"',
    ],
    fields: toFields(teacherPurchaseHelpTexts, {
      listName: 'שם הרשימה',
      budgetSource: 'מקור תקציב',
      notes: 'הערות',
      catalogSearch: 'חיפוש בקטלוג',
      adHocName: 'שם מוצר (ידני)',
      adHocQuantity: 'כמות',
      adHocUnit: 'יחידת מידה',
      adHocPrice: 'מחיר ליחידה',
      adHocChargeable: 'מוצר מחויב',
      convertSource: 'מקור תקציב להמרה',
      receivedQuantity: 'כמות שהתקבלה',
    }),
  },

  // --- אנשי קשר (הורים) ---
  {
    id: 'parent-contacts',
    title: 'ניהול אנשי קשר',
    icon: 'Contact',
    roles: ['parent'],
    routePattern: '/parent/contacts',

    introduction: 'מסך אנשי הקשר מאפשר להורה להוסיף ולנהל אנשי קשר נוספים (סבים, דודים, מטפלות) שיקבלו התראות על הילדים. ניתן לשייך כל איש קשר לילדים ספציפיים ולהגדיר סדר התראות.',
    steps: [
      'לחץ "הוסף איש קשר"',
      'הזן מספר טלפון (10 ספרות)',
      'הזן אימייל (אופציונלי)',
      'בחר יחס קרבה: הורה, קרוב משפחה, חבר, אחר',
      'סמן את הילדים שאיש הקשר יקבל עליהם התראות',
      'לחץ "שמירה"',
    ],
    tips: [
      'רק ההורה המשלם יכול לערוך הגדרות התראות של אנשי קשר',
      'ניתן להגדיר סדר התראות: ראשון, שני, או שניהם',
    ],
    fields: toFields(parentContactsHelpTexts, {
      contactPhone: 'טלפון',
      contactEmail: 'אימייל',
      relation: 'יחס קרבה',
      childrenSelection: 'שיוך ילדים',
    }),
  },

  // --- מוקד טלפוני ---
  {
    id: 'callcenter',
    title: 'מוקד טלפוני',
    icon: 'Headphones',
    roles: ['staff'],
    routePattern: '/callcenter',

    introduction: 'מסך המוקד הטלפוני מציג את כל ההתראות הפעילות. כל התראה עוברת מחזור חיים: ממתין (L1) → פתוח (L2) → בטיפול → טופל/שגוי. המוקדן יכול לחפש, לסנן ולטפל בהתראות. המסך מתרענן אוטומטית כל 15 שניות.',
    steps: [
      'צפה בטאבים: ממתין, פתוח, בטיפול, טופל, שגוי, הכל',
      'לחץ על התראה לפתיחת פרטים',
      'לקח התראה לטיפול — לחץ "טפל"',
      'בסיום: בחר סוג פתרון (טופל/שגוי) ותעד הערות',
      'לחץ "סגור" — ההתראה תעבור לסטטוס סופי',
    ],
    tips: [
      'חיפוש — לפי שם ילד, שם גן או שם הורה',
      'סינון — לפי סטטוס או רמת דחיפות',
      'רענון אוטומטי — כל 15 שניות',
      'התראות מתבטלות אוטומטית כשילד מסומן "הגיע" בטאבלט',
    ],
    fields: toFields(callCenterHelpTexts, {
      search: 'חיפוש',
      statusFilter: 'סינון סטטוס',
      priorityFilter: 'סינון דחיפות',
      resolutionNotes: 'הערות פתרון',
      resolutionType: 'סוג פתרון',
    }),
  },

  // --- כניסת צוות גן (טאבלט) ---
  {
    id: 'tablet-login',
    title: 'כניסה למערכת — צוות גן',
    icon: 'LogIn',
    roles: ['tablet'],
    routePattern: '/tablet/login',

    introduction: 'מסך הכניסה לטאבלט הגן. הכניסה דורשת שלושה שדות: קוד הגן (שהועבר מהמנהל/ת), שם משתמש וסיסמה. לאחר כניסה מוצלחת תועברו למסך הנוכחות.',
    steps: [
      'הזן קוד גן (אותיות ומספרים — ניתן לקבל מהמנהל/ת)',
      'הזן שם משתמש',
      'הזן סיסמה — ודא שהמקלדת באנגלית',
      'לחץ "כניסה" — תועבר למסך הנוכחות של הגן',
    ],
    tips: [
      'קוד הגן הוא קוד ייחודי לכל גן — ניתן לקבלו ממנהל/ת הרשת',
      'אם הכניסה נכשלת — בדקו שהמקלדת באנגלית ושאין Caps Lock',
      'לאחר 5 ניסיונות כושלים החשבון ייחסם ל-15 דקות',
    ],
    fields: [],
  },

  // --- מסך נוכחות — לוח מחוונים (טאבלט) ---
  {
    id: 'tablet-dashboard',
    title: 'מסך נוכחות — לוח מחוונים',
    icon: 'Users',
    roles: ['tablet'],
    routePattern: '/tablet/dashboard',

    introduction: 'מסך הנוכחות הראשי של הגן. המסך מציג שני מצבי תצוגה: "טרם הגיעו" (ילדים שצפויים להגיע) ו-"נמצאים בגן" (ילדים שכבר הגיעו). לחיצה על כרטיס ילד מסמנת הגעה או עזיבה. המסך עובר אוטומטית בין המצבים לפי שעת הפתיחה שהוגדרה בהגדרות הגן.',
    steps: [
      'בבוקר — מסך "טרם הגיעו" מציג את כל הילדים הצפויים להיום',
      'לחצו על כרטיס ילד כדי לסמן הגעה — הילד יעבור ל-"נמצאים בגן"',
      'לחצו על כפתור "נמצאים בגן ←" כדי להחליף תצוגה',
      'במסך "נמצאים בגן" — לחצו על כרטיס ילד כדי לסמן עזיבה',
      'השתמשו בשדה החיפוש כדי למצוא ילד לפי שם',
      'לחצו על כפתור "הודעות" לשליחת הודעה להורים',
      'לחצו על כפתור "קניות" למעבר לרשימות קניות',
    ],
    tips: [
      'המסך עובר אוטומטית מ-"טרם הגיעו" ל-"נמצאים בגן" לפי שעת פתיחת הגן',
      'סימון הגעה מבטל אוטומטית התראות חסרים שנשלחו להורים',
      'ניתן לשלוח הודעות להורה של ילד ספציפי או לכל ההורים',
      'בשליחת הודעה — ניתן לסמן "שלח גם SMS" לנייד',
      'ניתן ליצור ולשמור תבניות הודעה לשימוש חוזר',
      'סנכרון מלא מתבצע אוטומטית בכל בוקר',
    ],
    fields: [],
  },

  // --- רשימות קניות (טאבלט) ---
  {
    id: 'tablet-purchases',
    title: 'רשימות קניות',
    icon: 'ShoppingCart',
    roles: ['tablet'],
    routePattern: '/tablet/purchases',

    introduction: 'מסך ניהול רשימות הקניות של הגן. ניתן ליצור רשימות קניות חדשות, להוסיף מוצרים מהקטלוג או באופן חופשי, לעדכן סטטוס ולמחוק פריטים. כל רשימה משויכת למקור תקציב.',
    steps: [
      'צפה ברשימות הקניות הקיימות — סנן לפי סטטוס (טיוטה / ממתין / מאושר / הושלם)',
      'לחץ "רשימה חדשה" ליצירת רשימת קניות — בחר שם ומקור תקציב',
      'לחץ על רשימה קיימת כדי לצפות בפרטיה',
      'הוסף מוצרים מהקטלוג — חפש לפי שם או קטגוריה',
      'הוסף מוצר חופשי — הזן שם, כמות, יחידה ומחיר',
      'סמן פריט כ-"נרכש" בלחיצה על V',
      'מחק פריט בלחיצה על פח האשפה',
    ],
    tips: [
      'ניתן לחזור למסך הנוכחות בלחיצה על חץ חזור בכותרת',
      'רשימה בסטטוס "טיוטה" ניתנת לעריכה מלאה',
      'ניתן להוסיף הערות לכל רשימת קניות',
      'הסכום הכולל מתעדכן אוטומטית בעת הוספה או מחיקה של פריטים',
    ],
    fields: [],
  },
];

// ===== פונקציות עזר =====

/** מחזיר סקציות מסוננות לפי תפקיד */
export function getGuideSectionsForRole(role: UserRole): GuideSection[] {
  return guideSections.filter(s => s.roles.includes(role));
}

/** מוצא סקציה לפי route pattern */
export function findSectionByRoute(path: string): GuideSection | undefined {
  return guideSections.find(s => {
    if (!s.routePattern) return false;
    try {
      return new RegExp(s.routePattern).test(path);
    } catch {
      return path.includes(s.routePattern);
    }
  });
}
